type LedgerTranslate = {
  (key: string, defaultValue?: string): string;
  (key: string, defaultValue: string, options: Record<string, unknown>): string;
  (key: string, options: Record<string, unknown>): string;
};

/** Legacy Me menu links use short action keys; API expects ledger action ids. */
export const LEDGER_ACTION_QUERY_ALIASES: Record<string, string> = {
  activation: 'activation_reward',
  uoc_withdraw: 'uoc_burn',
};

export function normalizeLedgerActionQuery(action: string): string {
  const trimmed = action.trim();
  if (!trimmed) return '';
  return LEDGER_ACTION_QUERY_ALIASES[trimmed] ?? trimmed;
}

export function ledgerActionLabels(t: LedgerTranslate): Record<string, string> {
  return {
    recharge: t('history.action_recharge', '充值'),
    withdraw: t('history.action_withdraw', '提现'),
    transfer_in: t('history.action_transfer_in', '转入'),
    transfer_out: t('history.action_transfer_out', '转出'),
    ep_trade: t('history.action_ep_trade', 'EP交易'),
    ep_escrow: t('history.action_ep_escrow', 'EP锁仓'),
    daily_settlement: t('history.action_daily_settlement', '每日结算'),
    mentorship_bonus: t('history.action_mentorship_bonus', '辅导奖'),
    share_bonus: t('history.action_share_bonus', '分享级差奖'),
    share_bonus_diff: t('history.action_share_bonus_diff', '分享奖'),
    share_bonus_peer: t('history.action_share_bonus_peer', '分享平级奖'),
    balance_bonus: t('history.action_balance_bonus', '平衡奖'),
    peer_level_bonus: t('history.action_peer_level_bonus', '平级奖'),
    level_upgrade_fee: t('history.action_level_upgrade_fee', '升级费'),
    convert_in: t('history.action_convert_in', '兑换入'),
    convert_out: t('history.action_convert_out', '兑换出'),
    internal_convert_in: t('history.action_convert_in', '兑换入'),
    internal_convert_out: t('history.action_convert_out', '兑换出'),
    mp_convert_out: t('history.action_mp_convert_out', 'MP兑换ORB'),
    mp_convert_uo: t('history.action_mp_convert_uo', 'MP兑换ORB'),
    split_pool_in: t('history.action_split_pool_in', '分裂入账'),
    split_pool_out: t('history.action_split_pool_out', '分裂出账'),
    admin_adjust: t('history.action_admin_adjust', '管理员调整'),
    imputation_in: t('history.action_imputation', '一键归集'),
    imputation_out: t('history.action_imputation', '一键归集'),
    pcc_reward: t('history.action_pcc_reward', 'PCC奖励'),
    activation_reward: t('history.action_activation_reward', '激活奖励'),
    activation: t('history.action_activation_reward', '激活奖励'),
    honor_reward: t('history.action_honor_reward', '荣耀奖励'),
    fork_reward: t('history.action_fork_reward', '复制奖'),
    uoc_mint: t('history.action_uoc_mint', 'ORBC铸造'),
    uoc_burn: t('history.action_uoc_withdraw', 'ORBC提币'),
    uoc_withdraw: t('history.action_uoc_withdraw', 'ORBC提币'),
    otc_lock: t('history.action_otc_lock', 'ORB交易锁仓'),
    otc_settle: t('history.action_otc_settle', 'ORB交易成交'),
    otc_refund: t('history.action_otc_refund', 'ORB交易退款'),
    otc_fee_ep: t('history.action_otc_fee_ep', 'ORB交易手续费'),
    static_usdt_payout: t('history.action_static_usdt_payout', '静态USDT兑付'),
    static_usdt_payout_refund: t('history.action_static_usdt_payout_refund', '静态USDT兑付退回'),
  };
}

export function formatLedgerAction(action: string, t: LedgerTranslate): string {
  return ledgerActionLabels(t)[action] ?? action;
}

const ASSET_ZH: Record<string, string> = {
  uo: 'ORB',
  uoc: 'ORBC',
  ep: 'EP',
  ep1: 'EP1',
  ep2: 'EP2',
  rp: 'RP',
  tp: 'TP',
  mp: 'MP',
  fp: 'FP',
  fc: 'FC',
  sp1: 'SP1',
  sp2: 'SP2',
  sp3: 'SP3',
  iep: 'IEP',
  ap: 'AP',
};

function assetZh(raw: string): string {
  const k = raw.trim().toLowerCase();
  return ASSET_ZH[k] ?? raw.trim().toUpperCase();
}

/**
 * Localize ledger `note` for display (existing English rows + keep Chinese as-is).
 */
export function formatLedgerNote(
  note: string | null | undefined,
  t: LedgerTranslate,
): string {
  const raw = (note ?? '').trim();
  if (!raw) return '';

  let m = raw.match(/^lineage\s+(down|up)\s+(\w+)$/i);
  if (m) {
    const dir =
      m[1].toLowerCase() === 'down'
        ? t('history.note_lineage_down', '转至下级')
        : t('history.note_lineage_up', '转至上级');
    return `${dir} ${assetZh(m[2])}`;
  }

  m = raw.match(/^lineage\s+TP\s+fee\s+from\s+user\s+#(\d+)$/i);
  if (m) {
    return t('history.note_lineage_tp_fee', 'TP手续费（来自用户 #{{id}}）', {
      id: m[1],
    });
  }

  if (/^RP→SP3\s*\(level quota\)$/i.test(raw) || /^RP->SP3\s*\(level quota\)$/i.test(raw)) {
    return t('history.note_rp_sp3_quota', 'RP转SP3（等级额度）');
  }

  m = raw.match(/^RP→SP3:\s*(.+)$/i) || raw.match(/^RP->SP3:\s*(.+)$/i);
  if (m) {
    return t('history.note_rp_sp3_memo', 'RP转SP3：{{memo}}', {
      memo: m[1],
    });
  }

  m = raw.match(/^([a-z][a-z0-9]*)(?:\s*→\s*|\s*->\s*)([a-z][a-z0-9]*)$/i);
  if (m) {
    return t('history.note_convert_pair', '{{from}}转{{to}}', {
      from: assetZh(m[1]),
      to: assetZh(m[2]),
    });
  }

  m = raw.match(/^To\s+(.+)$/i);
  if (m) {
    return t('history.note_to_user', '转给 {{name}}', { name: m[1] });
  }

  m = raw.match(/^From\s+(.+)$/i);
  if (m) {
    return t('history.note_from_user', '来自 {{name}}', { name: m[1] });
  }

  m = raw.match(/^MP→ORB:\s*(.+)$/i) || raw.match(/^MP->ORB:\s*(.+)$/i);
  if (m) {
    return t('history.note_mp_orb_memo', 'MP兑换ORB：{{memo}}', {
      memo: m[1],
    });
  }

  if (/^MP→ORB/i.test(raw) || /^MP->ORB/i.test(raw)) {
    return t('history.note_mp_orb', 'MP兑换ORB');
  }

  return raw;
}

/** Prefer legacy remark text for imputation / 归集 rows; split share-bonus kinds. */
export function resolveLedgerRowTitle(
  action: string,
  note: string | null | undefined,
  t: LedgerTranslate,
): string {
  const remark = (note ?? '').trim();
  if (remark.includes('归集') || remark.includes('一键归集')) {
    return t('history.action_imputation', '一键归集');
  }
  if (action === 'share_bonus' || /^EP2RS:001/i.test(remark)) {
    if (remark.includes('平级') || /EP2RS:001\[平级/i.test(remark)) {
      return t('history.action_share_bonus_peer', '分享平级奖');
    }
    if (remark.includes('级差') || /EP2RS:001\[级差/i.test(remark)) {
      return t('history.action_share_bonus_diff', '分享奖');
    }
    // Legacy unmarked EP2RS:001 rows — treat as differential share.
    if (/^EP2RS:001/i.test(remark)) {
      return t('history.action_share_bonus_diff', '分享奖');
    }
    return t('history.action_share_bonus', '分享级差奖');
  }
  return formatLedgerAction(action, t);
}

export function formatLedgerDelta(delta: string | null | undefined): {
  text: string;
  isNegative: boolean;
} {
  const raw = (delta ?? '0').trim() || '0';
  const isNegative = raw.startsWith('-');
  // Drop trailing zeros / lone "." so integers show as +50 not +50.
  const normalized = raw.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  return { text: isNegative ? normalized : `+${normalized}`, isNegative };
}
